/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type Map from 'ol/Map';
import { unByKey } from 'ol/Observable';
import { transformExtent } from 'ol/proj';
import React from 'react';

const STAC_VIEWPORT_BUFFER_RATIO = 0.25;
const MAX_REUSED_BBOX_SIZE_RATIO = 4;
// Only one COG is rendered at a time, so raster rendering can start while the
// viewport is still broad enough to provide useful geographic context.
export const STAC_TILED_MINIMUM_ZOOM = 10;
const MAX_STAC_QUERY_WIDTH_DEGREES = 120;
const MAX_STAC_QUERY_HEIGHT_DEGREES = 90;

const containsExtent = (container: number[], extent: number[]): boolean =>
  container[0] <= extent[0] &&
  container[1] <= extent[1] &&
  container[2] >= extent[2] &&
  container[3] >= extent[3];

const isBboxSizeReusable = (
  currentBbox: number[],
  viewportBbox: number[],
): boolean => {
  const currentWidth = currentBbox[2] - currentBbox[0];
  const currentHeight = currentBbox[3] - currentBbox[1];
  const viewportWidth = viewportBbox[2] - viewportBbox[0];
  const viewportHeight = viewportBbox[3] - viewportBbox[1];

  return (
    currentWidth <= viewportWidth * MAX_REUSED_BBOX_SIZE_RATIO &&
    currentHeight <= viewportHeight * MAX_REUSED_BBOX_SIZE_RATIO
  );
};

const roundCoordinate = (coordinate: number): number =>
  Number(coordinate.toFixed(5));

const getViewportBbox = (map: Map): number[] | undefined => {
  const size = map.getSize?.();
  const view = map.getView?.();
  if (!size || !view?.calculateExtent) {
    return undefined;
  }

  const projection = view.getProjection();
  const extent = transformExtent(
    view.calculateExtent(size),
    projection,
    'EPSG:4326',
    8,
  );
  if (!extent.every(Number.isFinite)) {
    return undefined;
  }

  return extent.map(roundCoordinate);
};

const bufferBbox = (
  extent: number[],
  bufferRatio = STAC_VIEWPORT_BUFFER_RATIO,
): number[] => {
  const width = extent[2] - extent[0];
  const height = extent[3] - extent[1];
  if (width >= 360) {
    return [-180, -90, 180, 90];
  }

  return [
    Math.max(-180, extent[0] - width * bufferRatio),
    Math.max(-90, extent[1] - height * bufferRatio),
    Math.min(180, extent[2] + width * bufferRatio),
    Math.min(90, extent[3] + height * bufferRatio),
  ].map(roundCoordinate);
};

export const getStacViewportBbox = (map: Map): number[] | undefined => {
  const extent = getViewportBbox(map);
  return extent ? bufferBbox(extent) : undefined;
};

export const isStacViewportBboxQueryable = (
  bbox?: number[],
): bbox is number[] =>
  Boolean(
    bbox?.length === 4 &&
    bbox.every(Number.isFinite) &&
    bbox[2] - bbox[0] <= MAX_STAC_QUERY_WIDTH_DEGREES &&
    bbox[3] - bbox[1] <= MAX_STAC_QUERY_HEIGHT_DEGREES,
  );

export const getNextStacViewportBbox = (
  currentBbox: number[] | undefined,
  viewportBbox: number[],
  bufferRatio = STAC_VIEWPORT_BUFFER_RATIO,
): number[] => {
  if (bufferRatio === 0) {
    return viewportBbox;
  }

  if (
    currentBbox &&
    isStacViewportBboxQueryable(currentBbox) &&
    isBboxSizeReusable(currentBbox, viewportBbox) &&
    containsExtent(currentBbox, viewportBbox)
  ) {
    return currentBbox;
  }

  return bufferBbox(viewportBbox, bufferRatio);
};

export const useStacViewportBbox = (
  map: Map | null,
  enabled: boolean,
  minimumZoom?: number,
  bufferRatio = STAC_VIEWPORT_BUFFER_RATIO,
): number[] | undefined => {
  const [bbox, setBbox] = React.useState<number[] | undefined>();

  React.useEffect(() => {
    if (!map || !enabled) {
      setBbox(undefined);
      return (): void => {};
    }

    const updateBbox = (): void => {
      const zoom = map.getView?.().getZoom?.();
      if (
        minimumZoom !== undefined &&
        (zoom === undefined || zoom < minimumZoom)
      ) {
        setBbox(undefined);
        return;
      }

      const viewportBbox = getViewportBbox(map);
      if (!viewportBbox) {
        return;
      }
      setBbox((currentBbox) =>
        getNextStacViewportBbox(currentBbox, viewportBbox, bufferRatio),
      );
    };

    updateBbox();
    const eventKey = map.on('moveend', updateBbox);
    return (): void => {
      eventKey && unByKey(eventKey);
    };
  }, [bufferRatio, enabled, map, minimumZoom]);

  return bbox;
};
